import React, { useContext, useEffect, useRef, useState } from 'react'
import Table, { ColumnProps } from '../../Common/Table';
import { Cell, Grid, Switch } from 'react-foundation';
import type { PageFilters, Sortable } from '../../../models/interfaces'
import { ServiceRequestContext } from '../ServiceRequestContext';
import { ApiFetch, ApiPost, ApiPut } from '../../Common/Api';
import { Pageable } from '../../../models';
import { SearchPreferences } from '../../../models/UserPreferences';

type ProviderSearchFilter = {
    inNetwork: boolean
    inServiceArea: boolean
    hasSpecialtyCategory: boolean
    serviceRequestServiceID: number;
    memberID: string;
} & PageFilters & Sortable

type SavedProviderSearchFilter = Omit<ProviderSearchFilter, 'rowIndex'>;

type ProviderSearchResult = {
    userID: number;
    name: string;
    responseStatus: string;
    distance: number | null;
    inNetwork: boolean;
    hasSpecialtyCategory: boolean;
    inServiceArea: boolean;
    location: string;
    score: number;
}

const defaultProviderSearchFilter: ProviderSearchFilter = {
    inNetwork: true,
    inServiceArea: true,
    hasSpecialtyCategory: true,
    serviceRequestServiceID: 0,
    memberID: "",
    rowIndex: 0,
    pageSize: 50,
    sort: []
};

type ProviderSearchProps = {
    isActive?: boolean;
}

const ProviderSearch: React.FC<ProviderSearchProps> = ({ isActive = true }) => {
    const { readOnly, selectedService } = useContext(ServiceRequestContext);
    const [providers, setProviders] = useState<Pageable<ProviderSearchResult>>({ totalRows: 0, results: []});
    const [selectedProviders, setSelectedProviders] = useState<Set<number>>(new Set<number>());
    const [hideOutOfNetwork, setHideOutOfNetwork] = useState<boolean>(true);
    const [filters, setFilters] = useState<ProviderSearchFilter>(defaultProviderSearchFilter);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isSavingFilters, setIsSavingFilters] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const hasLoadedRef = useRef<boolean>(false);

    const renderResponseStatus = (status: string) => {
        switch (status.toLowerCase()) {
            case 'nointerest':
                return 'Declined';
            case 'interested':
                return 'Accepted';
            case 'noresponse':
                return 'Opted Out';
            default:
                return status;
        }
    }
    const columns: ColumnProps<ProviderSearchResult> = [
        { title: 'Select All', dataIndex: 'userID', render: (v) =>  (<input type="checkbox" className="custom-checkbox" checked={selectedProviders.has(v)} style={{ pointerEvents: 'none'}} readOnly></input>), headRender: () => (<label className='select-label'>Select All<br></br><input type="checkbox" className="custom-checkbox" onChange={(e) => {
            if (e.target.checked) {
                if (hideOutOfNetwork) {
                    setSelectedProviders(new Set(providers.results.filter(p => p.inNetwork).map(p => p.userID)));
                } else {
                    setSelectedProviders(new Set(providers.results.map(p => p.userID)));
                }
            } else {
                setSelectedProviders(new Set());
            }
        }} checked={hideOutOfNetwork ? selectedProviders.size === providers.results.filter(p => p.inNetwork).length && providers.results.length > 0 : selectedProviders.size === providers.results.length && providers.results.length > 0 }></input></label>), className: "center" },
        { title: 'Provider', dataIndex: 'name', sortable: true },
        { title: 'Response', dataIndex: 'responseStatus', render: renderResponseStatus, sortable: true },
        { title: 'Distance \n (miles)', dataIndex: 'distance', render: (value: number | null) => value === null ? 'Unavailable' : value, sortable: true, className: "short-column" },
        { title: 'In\n Network', dataIndex: 'inNetwork', render: (v) => <i className={`fas ${v ? 'fa-check-circle' : 'fa-times-circle'} selected`}></i>, sortable: false, className: "short-column"},
        { title: 'In Service\n Area', dataIndex: 'inServiceArea', render: (v) => <i className={`fas ${v ? 'fa-check-circle' : 'fa-times-circle'} selected`}></i>, sortable: true, className: "short-column" },
        { title: 'Specialty', dataIndex: 'hasSpecialtyCategory', render: (v) => <i className={`fas ${v ? 'fa-check-circle' : 'fa-times-circle'} selected`}></i>, sortable: true, className: "short-column" },
        { title: 'Location', dataIndex: 'location', sortable: false },
    ]

    const getColor = (row: ProviderSearchResult) => {
        if (row.score >= 0.85) return 'high-score';
        if (row.score >= 0.7) return 'mid-score';
        return '';
    }

    const loadProviders = () => {
        if (error) setError("");
        setIsLoading(true);
        ApiFetch<Pageable<ProviderSearchResult>>('ServiceRequestService/ProviderSearch', {...filters,  serviceRequestServiceID: selectedService?.serviceRequestServiceID, memberID: selectedService?.member?.memberID })
        .then((providers) => {
            setProviders(providers);
        })
        // eslint-disable-next-line no-console
        .catch((e) => {console.log(e); setError("An error occurred while fetching providers.")})
        .finally(() => {setIsLoading(false)})
    }

    const sendNotifications = () => {
        if (selectedService?.serviceRequestServiceID !== undefined) {
            if (error) setError("");
            setIsSending(true)
            ApiPost<void>(`ServiceRequestService/${selectedService?.serviceRequestServiceID}/notify`, undefined, { userIDs: Array.from(selectedProviders) })
                .then(() => {
                    setSelectedProviders(new Set());
                    loadProviders();
                })
                .catch(console.log)
                .finally(() => {setIsSending(false)})
        }
    }

    const saveFilters = () => {
        if (error) setError("");
        setIsSavingFilters(true);
        //remove rowIndex
        const { rowIndex: _rowIndex, ...savedFilters } = { ...filters, inNetwork: hideOutOfNetwork };
        ApiPut<void>(`User/SearchPreferences`, undefined, { providerSearchFilter: savedFilters as SavedProviderSearchFilter })
            .then(() => {
                console.log("successfully changed search preferences");
            })
            .catch(console.log)
            .finally(() => {setIsSavingFilters(false)})
    }

    const loadFilters = () => {
        if (error) setError("");
        ApiFetch<SearchPreferences>(`User/SearchPreferences`)
            .then((r) => {
                if (r && r.providerSearchFilter) {
                    r.providerSearchFilter.rowIndex = 0;
                    setFilters(r.providerSearchFilter) //state update will trigger loadProviders
                    if (r.providerSearchFilter.inNetwork !== undefined && r.providerSearchFilter.inNetwork !== hideOutOfNetwork) 
                        setHideOutOfNetwork(r.providerSearchFilter.inNetwork)
                } else {
                   loadProviders()
                }
            })
            .catch((e) => {console.log(e); loadProviders()})
    }
    
    const resetSort = () => {
        setFilters(prev => ({...prev, sort: [], rowIndex: 0}))
    }

    const handleTableFiltersChange: React.Dispatch<ProviderSearchFilter> = (newFilters) => {
        const isPaginationChange = newFilters.rowIndex !== filters.rowIndex || newFilters.pageSize !== filters.pageSize;
        setFilters(isPaginationChange ? newFilters : { ...newFilters, rowIndex: 0 });
    };

    //only run the search when the tab is opened
    useEffect(() => {
      if (isActive && hasLoadedRef.current) {
        loadProviders();
      }
    }, [filters])

    useEffect(() => {
      if (isActive && !hasLoadedRef.current) {
        loadFilters();
        hasLoadedRef.current = true;
      }
    }, [isActive])
    return(
        <div className="provider-search">
            <Grid>
                <Cell small={3}>
                    In-Network only
                    <Switch
                        input={{
                        name: 'inNetworkOnly',
                        checked: hideOutOfNetwork,
                        onChange: (_) => {
                            setHideOutOfNetwork(v => {
                                const newValue = !v;
                                if (newValue) {
                                    setSelectedProviders(prev => {
                                        const inNetworkProviderIDs = new Set(
                                            providers.results.filter(p => p.inNetwork).map(p => p.userID)
                                        );
                                        return new Set(Array.from(prev).filter(id => inNetworkProviderIDs.has(id)));
                                    });
                                }
                                setFilters(prev => ({ ...prev, inNetwork: newValue, rowIndex: 0 }));
                                return newValue;
                            });
                        }
                        }}
                    />
                </Cell>
                <Cell small={3}>
                    In Service Area
                    <Switch
                        input={{
                        name: 'inServiceAreaOnly',
                        checked: filters.inServiceArea,
                        onChange: (_) => setFilters(prev => ({...prev, inServiceArea: !prev.inServiceArea, rowIndex: 0}))
                        }}
                    />
                </Cell>
                <Cell small={3}>
                    Has Specialty
                    <Switch
                        input={{
                        name: 'hasSpecialCategory',
                        checked: filters.hasSpecialtyCategory,
                        onChange: (_) => setFilters(prev => ({...prev, hasSpecialtyCategory: !prev.hasSpecialtyCategory, rowIndex: 0}))
                        }}
                    />
                </Cell>
                <Cell small={3} className='center-button-container'>
                     <button disabled={isSavingFilters} onClick={() => resetSort()} className="send-button reset-button">
                            {isSavingFilters ? <div className="no-records send-loading">
                                    <img className='loading-img' src="/spinning-circles.svg" alt="loading" />
                                </div> : "Reset Sort"}
                    </button>
                    <button disabled={isSavingFilters} onClick={() => saveFilters()} className="send-button">
                            {isSavingFilters ? <div className="no-records send-loading">
                                    <img className='loading-img' src="/spinning-circles.svg" alt="loading" />
                                </div> : "Save Filters"}
                    </button>
                    
                </Cell>
            </Grid>
                <div className="score-legend">
                    <span className="score-legend-item"><span className="score-legend-swatch high-score-swatch"></span>Great match</span>
                    <span className="score-legend-item"><span className="score-legend-swatch mid-score-swatch"></span>Good match</span>
                </div>
                <Table
                    loading={isLoading}
                    columns={columns}
                    data={providers?.results}
                    rowClassName={getColor}
                    totalRows={providers?.totalRows}
                    filters={filters}
                    setFilters={handleTableFiltersChange}
                    defaultPageSize={filters.pageSize ?? defaultProviderSearchFilter.pageSize}
                    onRowClick={(row) => {
                        setSelectedProviders(prev => {
                            const next = new Set(prev);
                            next.has(row.userID) ? next.delete(row.userID) : next.add(row.userID);
                            return next;
                        });
                    } }
                    disableMultiColumnSort />
                <button disabled={isSending || readOnly || selectedProviders.size === 0 || selectedService?.status !== 'Active'} onClick={() => sendNotifications()} className="send-button send-notifications">
                        {isSending ? <div className='no-records send-loading'>
                                <img className='loading-img' src="/spinning-circles.svg" alt="loading" />
                            </div> : "Send Referrals"}
                </button>
               
                {error !== "" ? <p className="send-error">{error}</p> : null}
        </div>

    );
}
export default ProviderSearch
